import { createApp, h } from 'vue';
import Cookie from '@/service/cookie';
import loadingState from '@/plugins/loading';
// Namespace import + default-unwrap: CJS lodash under vite's optimizer
// can present either shape, and templates need the real object.
import * as lodashNS from 'lodash';
import App from './App.vue';
import router from './router';
import store from './store';
import { createBootstrap, Components as BvnComponents } from 'bootstrap-vue-next'; // Vue 3 successor of bootstrap-vue
import 'bootstrap-vue-next/dist/bootstrap-vue-next.css';
import Axios from 'axios';
import Notifications from '@kyvg/vue3-notification'; // Vue 3 fork, same $notify/<notifications> API
import vSelect from 'vue-select';
import { i18n } from '@/plugins/i18n';
import { Trans } from '@/plugins/Translation';
import VueTextareaAutosize from '@/plugins/textarea-autosize'; // local Vue 3 port
import ReactiveSearch from '@appbaseio/reactivesearch-vue';
import VModal from '@/plugins/modal'; // local Vue 3 replacement (name registry + $modal.show/hide)
import VueConfirmDialog from '@/plugins/confirm-dialog'; // local Vue 3 port
import { createGmapVuePlugin } from '@gmap-vue/v3';
import FloatingVue from 'floating-vue'; // v-tooltip's successor, same v-tooltip directive
import 'floating-vue/dist/style.css';
import Editor from '@/plugins/editor';
import Permissions from '@/plugins/vue-guard'; // local Vue 3 port of @waju/vue-guard
import VueFileAgent from '@boindil/vue-file-agent-next'; // community Vue 3 fork
import '@boindil/vue-file-agent-next/dist/vue-file-agent-next.css';
import interceptors from './service/interceptors';
import { searchRecorder, lastEnvelope } from '@/plugins/searchEnvelope';

/* CSS */
import '@/assets/style/style.scss';
import 'vue-select/dist/vue-select.css';


const app = createApp({
	data() {
		// Shared reactive object (src/plugins/loading.js): $root.loading keeps
		// working for views, and the router hooks toggle the same flags.
		return loadingState;
	},
	render: () => h(App),
});

app.use(router);
app.use(store);
app.use(i18n);

/* Plugins (Vue-2-era installs run under @vue/compat until each is migrated) */
app.use(Permissions);
app.use(createGmapVuePlugin({
	load: {
		key: process.env.VUE_APP_GOOGLE_MAPS_KEY,
		libraries: 'places',
	},
}));
app.use(Editor); // local Quill 2 wrapper serving the <ckeditor> tag (license-free)
app.use(VueFileAgent);
app.use(VueConfirmDialog);
app.use(ReactiveSearch); // v3 (Vue 3 line)
app.use(VueTextareaAutosize);
app.use(createBootstrap());
// bootstrap-vue-next registers NO components via createBootstrap() — the old
// BootstrapVue registered all of them globally, and our templates rely on that.
Object.entries(BvnComponents).forEach(([name, comp]) => app.component(name, comp));
app.use(Notifications);
app.use(VModal);
app.use(FloatingVue);

app.component('v-select', vSelect);

/* Vue.prototype.* -> globalProperties */
app.config.globalProperties.$http = Axios;
// Search goes through the reactivesearch-api translator (own auth);
// F3b: search goes through the Laravel proxy (live permission filters);
// no search credentials of any kind reach the browser. Token/Otp come from
// the same cookies the axios interceptor uses.
// Vue 2 exposed `_` to templates via the prototype chain. In Vue 3 the
// template proxy's dev context claims `_` for the internal instance, so
// the lodash template global must be named $_ instead (call sites rewritten).
app.config.globalProperties.$_ = lodashNS.default || lodashNS;

app.config.globalProperties.$searchEndpoint = (indexAlias) => {
	// X-Search-Authorization (not Authorization): the RS lib atob()s any
	// endpoint Authorization header assuming Basic auth and logs console
	// errors on Bearer tokens. The proxy remaps the header before auth.
	const headers = { 'X-Search-Authorization': 'Bearer ' + Cookie.get('token') };
	const otp = Cookie.get('otp');
	if (otp) headers.Otp = otp;
	return {
		url: `${process.env.VUE_APP_API_LINK}/api/search/${indexAlias}/_reactivesearch`,
		headers,
	};
};
app.config.globalProperties.$recordSearch = searchRecorder;
app.config.globalProperties.$lastEnvelope = lastEnvelope;
app.config.globalProperties.$i18nRoute = Trans.i18nRoute.bind(Trans);
// Verbatim Vue-2 helper bodies (i18n.locale -> i18n.global.locale for v9+ API)
app.config.globalProperties.$to = (object, label) => {
	var field = label + '_' + i18n.global.locale;
	return object[field] ? object[field] : object[label];
};
app.config.globalProperties.$label = () => {
	return i18n.global.locale == 'en' ? 'label_en' : 'label';
};
app.config.globalProperties.$field = (field) => {
	return i18n.global.locale == 'en' ? field + '_en' : field;
};

/* v-can: hide elements the user lacks permission for (bind -> beforeMount) */
app.directive('can', {
	beforeMount(el, binding) {
		if (!store.getters.permissions.includes(binding.value)) {
			el.style.display = 'none';
		}
	},
});

interceptors(store, router);

app.mount('#mount'); // App.vue's root carries id="app" (Vue 3 mounts inside, not replacing — avoids doubled #app)
